/* eslint-disable @next/next/no-img-element */
import CommonApi from '@/pages/api'
import TruncatedAnswer from '@/src/components/HomepageEdit/components/TruncatedElement'
import { Box, CircularProgress, Typography } from '@mui/material'
import React, { useEffect, useState, useCallback } from 'react'
import dummyImage from '@/src/assets/dummy/dummy.png'
import ContentThumbnail from '@/src/components/ContentThumbnail'
import { formatDate } from '@/src/types/helper'
import { useRouter } from 'next/router'
import Button from '@/src/components/button'
import { useStore } from '@/src/types/store'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

// Above this many articles the section scrolls horizontally instead of wrapping
// in a grid, matching the Social posts section (RL-2416).
const GRID_ITEM_LIMIT = 4

interface NewsItem {
  id: number
  headline: string
  summary: string | null
  publish_date?: string
  display_date: string
  thumbnail: string
  source: string
  source_icon: string
  link: string
  created_on: string
  updated_on: string
  business_id: number
  types?: string
  source_publish_at?: string
}

interface NewsSectionProps {
  sectionId: number
  isDraft?: boolean
  showNewsMenu?: boolean
}

const NewsSection = ({ sectionId, isDraft = false, showNewsMenu = true }: NewsSectionProps) => {
  const router = useRouter()
  const { TextColorType, themeLogo } = useStore()
  const [newsData, setNewsData] = useState<any>()
  const [loading, setLoading] = useState<boolean>(false)

  const fetchNewsData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await CommonApi({
        method: 'get',
        path: `show-pinned-content/${sectionId}${isDraft ? '?status=draft' : ''}`
      })
      if (response?.status) {
        setNewsData(response?.data?.pinned_news_articles)
      } else {
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }, [sectionId, isDraft])

  useEffect(() => {
    fetchNewsData()
  }, [fetchNewsData])

  const isCarousel = (newsData?.length ?? 0) > GRID_ITEM_LIMIT

  return (
    <div className={`w-full flex justify-center`}>
      {newsData?.length > 0 && (
        <div className="w-full grid grid-cols-1 gap-6">
          <div className="flex justify-between items-center">
            <Typography variant="h5">In the news</Typography>
            {showNewsMenu && (
              <Button
                onClick={() => router.push('/news-insights?types=web%2Cpdf')}
                variant="outlined"
                text="More articles"
                extraClass={`rounded-[100px] ${
                  TextColorType?.primaryTextDark
                    ? 'text-primary-home border-primary-home'
                    : TextColorType?.secondaryTextDark
                      ? 'text-secondary-home border-secondary-home'
                      : 'text-black border-black'
                }`}
                customStyle={true}
                iconPosition="right"
                icon={<ArrowForwardIcon />}
              />
            )}
          </div>
          {loading ? (
            <Box className="w-full flex justify-center items-center h-[300px]">
              <CircularProgress size={25} />
            </Box>
          ) : (
            <div
              className={
                isCarousel
                  ? 'w-full min-w-0 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3'
                  : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
              }
            >
              {newsData &&
                newsData?.map((item: NewsItem, index: number) => {
                  return (
                    <a
                      key={item?.id ?? `${item?.link ?? 'news'}-${index}`}
                      href={item?.link || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={isCarousel ? 'shrink-0 snap-start w-[85%] sm:w-[20rem]' : ''}
                    >
                      <div className="bg-white rounded-2xl relative flex flex-col h-full shadow-md">
                        <ContentThumbnail
                          item={item}
                          alt={item.headline || ''}
                          className="rounded-t-2xl w-full h-[190px] object-cover"
                          fallbackClassName="rounded-t-2xl w-full h-[190px] flex items-center justify-center bg-[#46586B4D]"
                        />
                        <div className="flex flex-col gap-4 flex-grow p-4">
                          <div className="flex flex-col gap-2">
                            <Typography variant="caption">
                              {formatDate(item?.display_date, 'long')}
                            </Typography>
                            <TruncatedAnswer
                              content={item?.headline}
                              textType="subtitle1"
                              maxLength={150}
                            />
                          </div>
                          <div className="mt-auto">
                            <div className={`flex justify-start items-center gap-1`}>
                              <div className="rounded-full">
                                <img
                                  src={
                                    typeof item?.source_icon === 'string'
                                      ? item?.source_icon
                                      : themeLogo?.logo_mark || dummyImage.src
                                  }
                                  alt={`${item?.source} icon`}
                                  className="rounded-full object-cover w-[40px] h-[40px]"
                                />
                              </div>
                              <Typography variant="subtitle2">{item?.source}</Typography>
                            </div>
                          </div>
                        </div>
                      </div>
                    </a>
                  )
                })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default NewsSection
